import { useState } from "react"
import { Link } from "react-router-dom"
import { DocumentTextIcon } from "@heroicons/react/24/outline"
import { Button } from "@/components/Button"
import { useToast } from "@/components/Toast"
import {
  useAffiliateStatements,
  useGenerateStatement,
  type AffiliateStatement,
} from "@/hooks/useAffiliateStatements"
import { APIError } from "@/api"
import {
  STATEMENT_STATUS_CLASS,
  formatCents,
  formatDateUtc,
  periodLabel,
  type StatementCadence,
} from "@/lib/affiliateStatements"

interface Props {
  publicId: string
  cadence: "none" | StatementCadence
}

export function StatementStatusBadge({ status }: { status: AffiliateStatement["status"] }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${STATEMENT_STATUS_CLASS[status]}`}>
      {status}
    </span>
  )
}

/**
 * A first_year_net affiliate's statements, plus the control to build the next one. Statements go
 * in order, so there is exactly one period that can be generated: the server names it.
 */
export function AffiliateStatements({ publicId, cadence }: Props) {
  const { toast } = useToast()
  // With cadence "none" and no statements yet, the cadence for a one-off statement is picked here.
  const [adHocCadence, setAdHocCadence] = useState<StatementCadence>("quarterly")
  const { data, isLoading, error } = useAffiliateStatements(
    publicId,
    true,
    cadence === "none" ? adHocCadence : undefined
  )
  const generate = useGenerateStatement()
  const [warnings, setWarnings] = useState<string[]>([])

  const statements = data?.statements ?? []
  const next = data?.next_period ?? null
  const nextLabel = next ? periodLabel(next.period_start, next.period_end) : null

  const onGenerate = async () => {
    if (!next) return
    try {
      const result = await generate.mutateAsync({
        affiliatePublicId: publicId,
        periodStart: next.period_start,
        ...(cadence === "none" ? { cadence: next.cadence } : {}),
      })
      setWarnings(result.warnings)
      toast({
        title: result.regenerated ? "Draft regenerated" : "Statement generated",
        description: periodLabel(result.statement.period_start, result.statement.period_end),
      })
    } catch (err) {
      setWarnings([])
      toast({
        title: "Error generating statement",
        description: err instanceof APIError ? err.originalError.message : "Request failed",
        variant: "destructive",
      })
    }
  }

  return (
    <section className="rounded-lg border border-border bg-surface p-5 space-y-4">
      <h2 className="text-lg font-semibold text-primary flex items-center gap-2">
        <DocumentTextIcon className="h-5 w-5 text-secondary" />
        Statements
      </h2>

      <div className="flex flex-col gap-2 rounded-md border border-border bg-background/40 p-3 sm:flex-row sm:items-end">
        {cadence === "none" && statements.length === 0 && (
          <div className="sm:w-40">
            <label htmlFor="statement-cadence" className="block text-xs text-secondary mb-1">
              Cadence
            </label>
            <select
              id="statement-cadence"
              value={adHocCadence}
              onChange={(e) => setAdHocCadence(e.target.value as StatementCadence)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm"
            >
              <option value="quarterly">Quarterly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
        )}
        <div className="min-w-0 flex-1 text-sm text-secondary">
          {isLoading
            ? "Checking the next period..."
            : !next
              ? "Set a statement cadence to generate statements."
              : next.has_draft
                ? `${nextLabel} is a draft. Open it to regenerate or send it; the next period can be generated once it is sent.`
                : `Next statement: ${nextLabel}${next.period_complete ? "" : " (in progress, a preview)"}.`}
        </div>
        {next && !next.has_draft && (
          <Button onClick={() => void onGenerate()} disabled={generate.isPending}>
            {generate.isPending ? "Generating..." : `Generate ${nextLabel}`}
          </Button>
        )}
      </div>
      <p className="text-xs text-secondary">
        Reads the period's payments and refunds from Stripe, which can take a few seconds. A
        statement for a period that is still running is a preview and can't be sent until the
        period ends.
      </p>

      {warnings.length > 0 && (
        <div className="rounded-md border border-amber-500/40 bg-amber-900/20 p-3 text-sm text-amber-300 space-y-1">
          <div className="font-medium">
            {warnings.length === 1 ? "1 warning" : `${warnings.length} warnings`} from the last
            generation
          </div>
          <ul className="list-disc pl-5 space-y-0.5">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {error ? (
        <p className="text-sm text-amber-400">Couldn't load statements.</p>
      ) : isLoading ? (
        <p className="text-sm text-secondary">Loading statements...</p>
      ) : statements.length === 0 ? (
        <p className="text-sm text-secondary">No statements yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-secondary border-b border-border">
                <th className="py-2 pr-2">Period</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2 text-right">Net revenue</th>
                <th className="py-2 pr-2 text-right">Commission</th>
                <th className="py-2 pr-2 text-right">Amount due</th>
                <th className="py-2 pr-2">Due by</th>
              </tr>
            </thead>
            <tbody>
              {statements.map((s) => (
                <tr key={s.public_id} className="border-b border-border/50">
                  <td className="py-2 pr-2">
                    <Link
                      to={`/affiliate-statements/${s.public_id}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {periodLabel(s.period_start, s.period_end)}
                    </Link>
                    {!s.period_complete && (
                      <span className="ml-2 text-xs text-secondary">in progress</span>
                    )}
                    {s.warnings.length > 0 && (
                      <span
                        className="ml-2 text-xs text-amber-400"
                        title={s.warnings.join("\n")}
                      >
                        {s.warnings.length === 1 ? "1 warning" : `${s.warnings.length} warnings`}
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-2">
                    <StatementStatusBadge status={s.status} />
                  </td>
                  <td className="py-2 pr-2 text-right">{formatCents(s.net_revenue_cents)}</td>
                  <td className="py-2 pr-2 text-right">{formatCents(s.commission_cents)}</td>
                  <td
                    className={`py-2 pr-2 text-right font-medium ${
                      s.amount_due_cents > 0 && s.status !== "paid" ? "text-amber-400" : ""
                    }`}
                  >
                    {formatCents(s.amount_due_cents)}
                  </td>
                  <td className="py-2 pr-2 text-secondary">{formatDateUtc(s.payment_due_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
